<template>
  <div class="feed-config-page container py-4">
    <!-- Global Loader Overlay -->
    <div
      v-if="loading.global"
      class="loading-overlay d-flex justify-content-center align-items-center"
    >
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <!-- Feed Selector -->
    <div class="mb-4 d-flex align-items-end">
      <label for="feedSelect" class="h6 font-weight-bold mr-2"
        >Selecione o Feed:</label
      >
      <select
        id="feedSelect"
        v-model="feed"
        @change="onFeedChange"
        class="form-control form-control-sm w-auto mr-2"
        :disabled="loading.feeds"
      >
        <option v-for="f in feeds" :key="`${f.name}-${f.country}`" :value="f">
          {{ f.name }}-{{ f.country }}
        </option>
      </select>
      <button
        class="btn btn-outline-primary btn-sm"
        @click="loadAll"
        :disabled="loading.global"
      >
        <span
          v-if="loading.global"
          class="spinner-border spinner-border-sm mr-1"
        ></span
        >Atualizar Dados
      </button>
    </div>

    <h2 class="mb-3 text-primary">
      Configuração: {{ feed.name }}-{{ feed.country }}
    </h2>

    <!-- Settings & Fees -->
    <div class="row mb-4 align-items-stretch">
      <!-- Settings Card -->
      <div
        v-if="!(feed.name === 'Worten' && feed.country === 'ES')"
        class="col-md-6 d-flex"
      >
        <div
          class="card shadow-sm h-100"
          :class="{ 'opacity-50': loading.settings }"
        >
          <div class="card-body position-relative py-3 d-flex flex-column">
            <div v-if="loading.settings" class="section-loader"></div>
            <h5 class="mb-1">Settings</h5>
            <p class="text-secondary mb-2">
              Defina os níveis mínimos de stock que o sistema deverá usar para
              cada fonte (OCP, Farmas, Engel). Ative 'Only Site' para usar
              exclusivamente o stock do site.
            </p>
            <div class="form-row mb-3 flex-grow-1">
              <div class="form-group col-6 mb-1">
                <label for="ocp_stock" class="col-form-label">OCP Stock</label>
                <input
                  type="number"
                  v-model.number="settings.ocp_stock"
                  class="form-control form-control-sm"
                  placeholder="OCP Stock"
                  :disabled="loading.settings"
                />
              </div>
              <div class="form-group col-6 mb-1">
                <label for="farmas_stock" class="col-form-label"
                  >Farmas Stock</label
                >
                <input
                  type="number"
                  v-model.number="settings.farmas_stock"
                  class="form-control form-control-sm"
                  placeholder="Farmas Stock"
                  :disabled="loading.settings"
                />
              </div>
              <div class="form-group col-6 mb-1">
                <label for="engel_stock" class="col-form-label"
                  >Engel Stock</label
                >
                <input
                  type="number"
                  v-model.number="settings.engel_stock"
                  class="form-control form-control-sm"
                  placeholder="Engel Stock"
                  :disabled="loading.settings"
                />
              </div>
              <div class="form-group col-6 mb-1 d-flex align-items-center">
                <!-- Only Site Switch -->
                <div class="custom-control custom-switch mx-auto">
                  <input
                    type="checkbox"
                    id="onlySite"
                    class="custom-control-input"
                    v-model="settings.only_site"
                    :disabled="loading.settings"
                  />
                  <label class="custom-control-label" for="onlySite"
                    >Only Site</label
                  >
                </div>
              </div>
            </div>
            <button
              class="btn btn-primary btn-sm mt-auto"
              @click="updateSettings"
              :disabled="loading.settings"
            >
              <span
                v-if="loading.settings"
                class="spinner-border spinner-border-sm mr-1"
              ></span
              >Guardar
            </button>
          </div>
        </div>
      </div>
      <!-- Fees Card -->
      <div class="col-md-6">
        <div class="card shadow-sm" :class="{ 'opacity-50': loading.fees }">
          <div class="card-body position-relative py-3">
            <div v-if="loading.fees" class="section-loader"></div>
            <h5 class="mb-1">Fees (%)</h5>
            <p class="text-secondary mb-2">
              Especifique a percentagem de taxa que será aplicada aos preços
              para este feed.
            </p>
            <div class="input-group input-group-sm w-75 mb-2">
              <input
                type="number"
                step="0.01"
                v-model.number="fees.fee"
                class="form-control"
                :disabled="loading.fees"
              />
              <div class="input-group-append">
                <button
                  class="btn btn-primary"
                  @click="updateFees"
                  :disabled="loading.fees"
                >
                  <span
                    v-if="loading.fees"
                    class="spinner-border spinner-border-sm mr-1"
                  ></span
                  >Guardar Fee
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Accordion Sections -->
    <div class="accordion">
      <!-- Price Intervals -->
      <div class="accordion-item">
        <div class="accordion-header" @click="toggleSection('intervals')">
          <h5 class="mb-0">Price Intervals</h5>
          <i
            class="fas"
            :class="expanded.intervals ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </div>
        <transition name="accordion">
          <div v-show="expanded.intervals" class="accordion-body">
            <!-- Column Descriptors -->
            <div class="d-flex mb-2 font-weight-bold align-items-center">
              <div
                class="form-control form-control-sm mr-1 text-center bg-light"
              >
                Start
              </div>
              <div
                class="form-control form-control-sm mr-1 text-center bg-light"
              >
                End
              </div>
              <div
                class="form-control form-control-sm mr-1 text-center bg-light"
              >
                Margin
              </div>
              <div
                class="form-control form-control-sm mr-1 text-center bg-light"
              >
                Mult
              </div>
              <div
                class="form-control form-control-sm mr-1 text-center bg-light"
              >
                Soma
              </div>
              <div class="btn btn-sm btn-outline-secondary mr-1 disabled">
                ✓
              </div>
              <div class="btn btn-sm btn-outline-secondary disabled">✕</div>
            </div>
            <div
              v-for="interval in intervals"
              :key="interval.id"
              class="d-flex mb-1 align-items-center"
            >
              <input
                type="number"
                v-model.number="interval.price_ini"
                class="form-control form-control-sm mr-1"
                :disabled="loading.intervals"
                placeholder="Start"
              />
              <input
                type="number"
                v-model.number="interval.price_final"
                class="form-control form-control-sm mr-1"
                :disabled="loading.intervals"
                placeholder="End"
              />
              <input
                type="number"
                v-model.number="interval.margin"
                class="form-control form-control-sm mr-1"
                :disabled="loading.intervals"
                placeholder="Margin"
              />
              <input
                type="number"
                v-model.number="interval.mult"
                class="form-control form-control-sm mr-1"
                :disabled="loading.intervals"
                placeholder="Mult"
              />
              <input
                type="number"
                v-model.number="interval.soma"
                class="form-control form-control-sm mr-1"
                :disabled="loading.intervals"
                placeholder="Soma"
              />
              <button
                class="btn btn-sm btn-success mr-1"
                @click="updateInterval(interval)"
                :disabled="loading.intervals"
              >
                ✓
              </button>
              <button
                class="btn btn-sm btn-danger"
                @click="deleteInterval(interval.id)"
                :disabled="loading.intervals"
              >
                ✕
              </button>
            </div>
            <div class="d-flex">
              <input
                type="number"
                v-model.number="newInterval.price_ini"
                class="form-control form-control-sm mr-1"
                placeholder="Start"
                :disabled="loading.intervals"
              />
              <input
                type="number"
                v-model.number="newInterval.price_final"
                class="form-control form-control-sm mr-1"
                placeholder="End"
                :disabled="loading.intervals"
              />
              <input
                type="number"
                v-model.number="newInterval.margin"
                class="form-control form-control-sm mr-1"
                placeholder="Margin"
                :disabled="loading.intervals"
              />
              <input
                type="number"
                v-model.number="newInterval.mult"
                class="form-control form-control-sm mr-1"
                placeholder="Mult"
                :disabled="loading.intervals"
              />
              <input
                type="number"
                v-model.number="newInterval.soma"
                class="form-control form-control-sm mr-1"
                placeholder="Soma"
                :disabled="loading.intervals"
              />
              <button
                class="btn btn-primary btn-sm"
                @click="addInterval"
                :disabled="
                  loading.intervals ||
                  !newInterval.price_ini ||
                  !newInterval.price_final ||
                  !newInterval.margin
                "
              >
                +
              </button>
            </div>
          </div>
        </transition>
      </div>

      <!-- Shipping -->
      <div class="accordion-item">
        <div class="accordion-header" @click="toggleSection('shipping')">
          <h5 class="mb-0">Shipping</h5>
          <i
            class="fas"
            :class="expanded.shipping ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </div>
        <transition name="accordion">
          <div v-show="expanded.shipping" class="accordion-body">
            <p class="text-secondary mb-2">
              Defina regras de envio com limites mínimos e máximos e o custo
              associado para cada faixa.
            </p>
            <div class="d-flex mb-2 font-weight-bold align-items-center">
              <div
                class="form-control form-control-sm mr-1 text-center bg-light"
              >
                Min
              </div>
              <div
                class="form-control form-control-sm mr-1 text-center bg-light"
              >
                Max
              </div>
              <div
                class="form-control form-control-sm mr-1 text-center bg-light"
              >
                Shipping
              </div>
              <div class="btn btn-sm btn-outline-secondary mr-1 disabled">
                ✓
              </div>
              <div class="btn btn-sm btn-outline-secondary disabled">✕</div>
            </div>
            <div
              v-for="ship in shippings"
              :key="ship.id"
              class="d-flex mb-1 align-items-center"
            >
              <input
                type="number"
                v-model.number="ship.minimo"
                class="form-control form-control-sm mr-1"
                :disabled="loading.shipping"
                placeholder="Min"
              />
              <input
                type="number"
                v-model.number="ship.maximo"
                class="form-control form-control-sm mr-1"
                :disabled="loading.shipping"
                placeholder="Max"
              />
              <input
                type="number"
                v-model.number="ship.shipping"
                class="form-control form-control-sm mr-1"
                :disabled="loading.shipping"
                placeholder="Shipping"
              />
              <button
                class="btn btn-sm btn-success mr-1"
                @click="updateShipping(ship)"
                :disabled="loading.shipping"
              >
                ✓
              </button>
              <button
                class="btn btn-sm btn-danger"
                @click="deleteShipping(ship.id)"
                :disabled="loading.shipping"
              >
                ✕
              </button>
            </div>
            <div class="d-flex">
              <input
                type="number"
                v-model.number="newShipping.minimo"
                class="form-control form-control-sm mr-1"
                placeholder="Min"
                :disabled="loading.shipping"
              />
              <input
                type="number"
                v-model.number="newShipping.maximo"
                class="form-control form-control-sm mr-1"
                placeholder="Max"
                :disabled="loading.shipping"
              />
              <input
                type="number"
                v-model.number="newShipping.shipping"
                class="form-control form-control-sm mr-1"
                placeholder="Shipping"
                :disabled="loading.shipping"
              />
              <button
                class="btn btn-primary btn-sm"
                @click="addShipping"
                :disabled="
                  loading.shipping ||
                  newShipping.minimo === null ||
                  newShipping.maximo === null ||
                  newShipping.shipping === null
                "
              >
                +
              </button>
            </div>
          </div>
        </transition>
      </div>

      <!-- No Products -->
      <div
        v-if="!(feed.name === 'Worten' && feed.country === 'ES')"
        class="accordion-item"
      >
        <div class="accordion-header" @click="toggleSection('noProducts')">
          <h5 class="mb-0">No Products</h5>
          <i
            class="fas"
            :class="expanded.noProducts ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </div>
        <transition name="accordion">
          <div v-show="expanded.noProducts" class="accordion-body">
            <p class="text-secondary mb-2">
              Liste produtos que deverão ser excluídos deste feed.
            </p>
            <ul class="list-group mb-2">
              <li
                v-for="prod in noProducts"
                :key="prod.id_prod"
                class="list-group-item p-1 d-flex justify-content-between align-items-center"
              >
                <small>{{ prod.name }} (SKU: {{ prod.sku }})</small
                ><button
                  class="btn btn-sm btn-danger"
                  @click="deleteNoProduct(prod.id_prod)"
                  :disabled="loading.noProducts"
                >
                  ✕
                </button>
              </li>
              <li
                v-if="!noProducts.length && !loading.noProducts"
                class="list-group-item p-1 text-muted"
              >
                <small>Sem produtos.</small>
              </li>
            </ul>
            <div class="d-flex">
              <input
                type="text"
                v-model="newNoProductSku"
                class="form-control form-control-sm mr-1"
                placeholder="Product SKU"
                :disabled="loading.noProducts"
              /><button
                class="btn btn-primary btn-sm"
                @click="addNoProduct"
                :disabled="loading.noProducts || !newNoProductSku.trim()"
              >
                +
              </button>
            </div>
          </div>
        </transition>
      </div>
      <!-- Manual Prices -->
      <div
        v-if="!(feed.name === 'Worten' && feed.country === 'ES')"
        class="accordion-item"
      >
        <div class="accordion-header" @click="toggleSection('manualPrices')">
          <h5 class="mb-0">Preços Manuais</h5>
          <i
            class="fas"
            :class="expanded.manualPrices ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </div>
        <transition name="accordion">
          <div v-show="expanded.manualPrices" class="accordion-body">
            <p class="text-secondary mb-2">
              Defina um preço fixo para produtos específicos neste feed, com
              data final opcional. Após essa data, o produto volta ao cálculo
              automático.
            </p>
            <ul class="list-group mb-2">
              <li
                v-for="prod in manualPrices"
                :key="prod.id_prod"
                class="list-group-item p-1 d-flex justify-content-between align-items-center"
              >
                <small>{{ prod.name }} (SKU: {{ prod.sku }})</small>
                <div class="d-flex align-items-center">
                  <input
                    type="number"
                    step="0.01"
                    v-model.number="prod.manual_price"
                    class="form-control form-control-sm mr-1"
                    style="width: 90px"
                    :disabled="loading.manualPrices"
                  />
                  <input
                    type="date"
                    v-model="prod.end_date"
                    class="form-control form-control-sm mr-1"
                    style="width: 140px"
                    :disabled="loading.manualPrices"
                  />
                  <button
                    class="btn btn-sm btn-success mr-1"
                    @click="updateManualPrice(prod)"
                    :disabled="loading.manualPrices"
                  >
                    ✓
                  </button>
                  <button
                    class="btn btn-sm btn-danger"
                    @click="deleteManualPrice(prod.id_prod)"
                    :disabled="loading.manualPrices"
                  >
                    ✕
                  </button>
                </div>
              </li>
              <li
                v-if="!manualPrices.length && !loading.manualPrices"
                class="list-group-item p-1 text-muted"
              >
                <small>Sem preços manuais definidos.</small>
              </li>
            </ul>
            <div class="d-flex">
              <input
                type="text"
                v-model="newManualPrice.sku"
                class="form-control form-control-sm mr-1"
                placeholder="Product SKU"
                :disabled="loading.manualPrices"
              />
              <input
                type="number"
                step="0.01"
                v-model.number="newManualPrice.price"
                class="form-control form-control-sm mr-1"
                placeholder="Preço (€)"
                :disabled="loading.manualPrices"
              />
              <input
                type="date"
                v-model="newManualPrice.end_date"
                class="form-control form-control-sm mr-1"
                placeholder="Data final (opcional)"
                :disabled="loading.manualPrices"
              />
              <button
                class="btn btn-primary btn-sm"
                @click="addManualPrice"
                :disabled="
                  loading.manualPrices ||
                  !newManualPrice.sku.trim() ||
                  !newManualPrice.price
                "
              >
                +
              </button>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
export default {
  name: 'FeedConfigPage',
  data() {
    return {
      feeds: [],
      feed: { name: 'KK', country: 'all' },
      settings: {
        ocp_stock: 0,
        farmas_stock: 0,
        engel_stock: 0,
        only_site: false,
      },
      fees: { fee: 0 },
      intervals: [],
      newInterval: {
        price_ini: null,
        price_final: null,
        margin: null,
        mult: 1,
        soma: 0,
      },
      shippings: [],
      newShipping: { minimo: null, maximo: null, shipping: null },
      noProducts: [],
      newNoProductSku: '',
      manualPrices: [],
      newManualPrice: { sku: '', price: null, end_date: null },
      loading: {
        global: false,
        feeds: false,
        settings: false,
        fees: false,
        intervals: false,
        shipping: false,
        noProducts: false,
        manualPrices: false,
      },
      expanded: {
        intervals: false,
        shipping: false,
        noProducts: false,
        manualPrices: false,
      },
    };
  },
  methods: {
    // Confirmation dialog that returns true if confirmed
    async confirmAdd(message) {
      const result = await this.$swal.fire({
        title: 'Adicionar?',
        text: message,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim!',
      });
      return result.isConfirmed;
    },
    // Confirmation dialog that returns true if confirmed
    async confirmUpdate(message) {
      const result = await this.$swal.fire({
        title: 'Atualizar?',
        text: message,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim!',
      });
      return result.isConfirmed;
    },
    // Confirmation dialog that returns true if confirmed
    async confirmDelete(message) {
      const result = await this.$swal.fire({
        title: 'Apagar?',
        text: message,
        icon: 'error',
        showCancelButton: true,
        confirmButtonText: 'Sim!',
      });
      return result.isConfirmed;
    },
    toggleSection(section) {
      this.expanded[section] = !this.expanded[section];
    },
    async fetchFeeds() {
      this.loading.feeds = true;
      this.feeds = [
        {
          name: 'KK',
          country: 'all',
        },
        {
          name: 'Bloop',
          country: 'all',
        },
        {
          name: 'Worten',
          country: 'PT',
        },
        {
          name: 'Worten',
          country: 'ES',
        },
        {
          name: 'Fnac',
          country: 'PT',
        },
        {
          name: 'Medis',
          country: 'PT',
        },
        {
          name: 'Temu',
          country: 'all',
        },
      ];
      this.feed = this.feeds[0];
      this.loading.feeds = false;
    },
    onFeedChange() {
      this.$router.replace({ params: { feed: this.feed } }).catch(() => {});
      this.loadAll();
    },
    async loadAll() {
      this.loading.global = true;
      const tasks = [
        this.loadSection('fees', this.fetchFees),
        this.loadSection('intervals', this.fetchIntervals),
        this.loadSection('shipping', this.fetchShippings),
      ];

      if (!(this.feed.name === 'Worten' && this.feed.country === 'ES')) {
        tasks.unshift(this.loadSection('settings', this.fetchSettings));
        tasks.unshift(this.loadSection('noProducts', this.fetchNoProducts));
        tasks.unshift(this.loadSection('manualPrices', this.fetchManualPrices));
      }

      await Promise.all(tasks);
      this.loading.global = false;
    },
    async loadSection(k, fn) {
      this.loading[k] = true;
      await fn();
      this.loading[k] = false;
    },
    async fetchSettings() {
      try {
        const { data } = await axios.get(
          `${this.$store.state.apiUrl}feed/settings/${this.feed.name}/${this.feed.country}`
        );
        this.settings = data.data.settings;
      } catch (error) {
        console.error('Error fetching settings:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao carregar definições',
          text: 'Por favor, tente novamente mais tarde.',
        });
      }
    },
    async updateSettings() {
      const ok = await this.confirmUpdate(
        'Tem a certeza que deseja atualizar as definições?'
      );
      if (!ok) return;
      this.loading.settings = true;
      try {
        await axios.patch(
          `${this.$store.state.apiUrl}feed/settings/${this.feed.name}/${this.feed.country}`,
          { settings: this.settings }
        );
        this.loading.settings = false;
        this.$swal.fire({
          toast: true,
          icon: 'success',
          title: 'Settings saved',
          timer: 2000,
          position: 'top-end',
        });
      } catch (error) {
        console.error('Error updating settings:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao atualizar definições',
          text: 'Por favor, tente novamente mais tarde.',
        });
        this.loading.settings = false;
        return;
      }
    },
    async fetchFees() {
      try {
        const { data } = await axios.get(
          `${this.$store.state.apiUrl}feed/fees/${this.feed.name}/${this.feed.country}`
        );
        this.fees = data.data.fees[0] || this.fees;
      } catch (error) {
        console.error('Error fetching fees:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao carregar fees',
          text: 'Por favor, tente novamente mais tarde.',
        });
      }
    },
    async updateFees() {
      const ok = await this.confirmUpdate(
        'Tem a certeza que deseja atualizar a fee?'
      );
      if (!ok) return;
      this.loading.fees = true;
      try {
        await axios.patch(
          `${this.$store.state.apiUrl}feed/fees/${this.feed.name}/${this.feed.country}`,
          {
            fees: this.fees,
          }
        );
        this.loading.fees = false;
        this.$swal.fire({
          toast: true,
          icon: 'success',
          title: 'Fee saved',
          timer: 2000,
          position: 'top-end',
        });
      } catch (error) {
        console.error('Error updating fees:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao atualizar fee',
          text: 'Por favor, tente novamente mais tarde.',
        });
        this.loading.fees = false;
        return;
      }
    },
    async fetchIntervals() {
      try {
        const { data } = await axios.get(
          `${this.$store.state.apiUrl}feed/priceInterval/${this.feed.name}/${this.feed.country}`
        );
        this.intervals = data.data.interval;
      } catch (error) {
        console.error('Error fetching intervals:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao carregar intervalos',
          text: 'Por favor, tente novamente mais tarde.',
        });
        return;
      }
    },
    async addInterval() {
      const ok = await this.confirmAdd(
        'Tem a certeza que deseja adicionar este intervalo?'
      );
      if (!ok) return;
      this.loading.intervals = true;
      try {
        await axios.post(
          `${this.$store.state.apiUrl}feed/priceInterval/${this.feed.name}/${this.feed.country}`,
          { interval: this.newInterval }
        );
        this.newInterval = {
          price_ini: null,
          price_final: null,
          margin: null,
          mult: null,
          soma: null,
        };
        await this.fetchIntervals();
      } catch (error) {
        console.error('Error adding interval:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao adicionar intervalo',
          text: 'Por favor, verifique os dados e tente novamente.',
        });
        this.loading.intervals = false;
        return;
      }
      this.loading.intervals = false;
    },
    async updateInterval(i) {
      const ok = await this.confirmUpdate(
        'Tem a certeza que deseja atualizar este intervalo?'
      );
      if (!ok) return;
      this.loading.intervals = true;
      try {
        await axios.patch(
          `${this.$store.state.apiUrl}feed/priceInterval/${this.feed.name}/${this.feed.country}`,
          { interval: i }
        );
        this.loading.intervals = false;
        this.$swal.fire({
          toast: true,
          icon: 'success',
          title: 'Interval updated',
          timer: 2000,
          position: 'top-end',
        });
      } catch (error) {
        console.error('Error updating interval:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao atualizar intervalo',
          text: 'Por favor, verifique os dados e tente novamente.',
        });
        this.loading.intervals = false;
        return;
      }
    },
    async deleteInterval(id) {
      const ok = await this.confirmDelete(
        'Tem a certeza que deseja eliminar este intervalo?'
      );
      if (!ok) return;
      this.loading.intervals = true;
      try {
        await axios.delete(
          `${this.$store.state.apiUrl}feed/priceInterval/${this.feed.name}/${this.feed.country}`,
          { data: { id } }
        );
        await this.fetchIntervals();
        this.loading.intervals = false;
      } catch (error) {
        console.error('Error deleting interval:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao eliminar intervalo',
          text: 'Por favor, tente novamente mais tarde.',
        });
        this.loading.intervals = false;
        return;
      }
    },
    async fetchShippings() {
      try {
        const { data } = await axios.get(
          `${this.$store.state.apiUrl}feed/shipping/${this.feed.name}/${this.feed.country}`
        );
        this.shippings = data.data.shipping;
      } catch (error) {
        console.error('Error fetching shippings:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao carregar envios',
          text: 'Por favor, tente novamente mais tarde.',
        });
      }
    },
    async addShipping() {
      const ok = await this.confirmAdd(
        'Tem a certeza que deseja adicionar este envio?'
      );
      if (!ok) return;
      this.loading.shipping = true;
      try {
        await axios.post(
          `${this.$store.state.apiUrl}feed/shipping/${this.feed.name}/${this.feed.country}`,
          { shipping: this.newShipping }
        );
        this.newShipping = { minimo: null, maximo: null, shipping: null };
        await this.fetchShippings();
        this.loading.shipping = false;
      } catch (error) {
        console.error('Error adding shipping:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao adicionar envio',
          text: 'Por favor, verifique os dados e tente novamente.',
        });
        this.loading.shipping = false;
        return;
      }
    },
    async updateShipping(s) {
      const ok = await this.confirmUpdate(
        'Tem a certeza que deseja atualizar este envio?'
      );
      if (!ok) return;
      this.loading.shipping = true;
      try {
        await axios.patch(
          `${this.$store.state.apiUrl}feed/shipping/${this.feed.name}/${this.feed.country}`,
          { shipping: s }
        );
        this.loading.shipping = false;
        this.$swal.fire({
          toast: true,
          icon: 'success',
          title: 'Shipping updated',
          timer: 2000,
          position: 'top-end',
        });
      } catch (error) {
        console.error('Error updating shipping:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao atualizar envio',
          text: 'Por favor, verifique os dados e tente novamente.',
        });
        this.loading.shipping = false;
        return;
      }
    },
    async deleteShipping(id) {
      const ok = await this.confirmDelete(
        'Tem a certeza que deseja eliminar este envio?'
      );
      if (!ok) return;
      this.loading.shipping = true;
      try {
        await axios.delete(
          `${this.$store.state.apiUrl}feed/shipping/${this.feed.name}/${this.feed.country}`,
          { data: { id } }
        );
        await this.fetchShippings();
        this.loading.shipping = false;
      } catch (error) {
        console.error('Error deleting shipping:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao eliminar envio',
          text: 'Por favor, tente novamente mais tarde.',
        });
        this.loading.shipping = false;
        return;
      }
    },
    async fetchNoProducts() {
      try {
        const { data } = await axios.get(
          `${this.$store.state.apiUrl}feed/noProducts/${this.feed.name}/${this.feed.country}`
        );
        this.noProducts = data.data.not_feed_products;
      } catch (error) {
        console.error('Error fetching no products:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao carregar produtos não incluídos',
          text: 'Por favor, tente novamente mais tarde.',
        });
        return;
      }
    },
    async addNoProduct() {
      const ok = await this.confirmAdd(
        'Tem a certeza que deseja adicionar este produto à lista de não produtos?'
      );
      if (!ok) return;
      this.loading.noProducts = true;
      try {
        await axios.post(
          `${this.$store.state.apiUrl}feed/noProducts/${this.feed.name}/${this.feed.country}`,
          { sku: this.newNoProductSku }
        );
        this.newNoProductSku = '';
        await this.fetchNoProducts();
        this.loading.noProducts = false;
      } catch (error) {
        console.error('Error adding no product:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao adicionar produto não incluído',
          text: 'Por favor, verifique o SKU e tente novamente.',
        });
        this.loading.noProducts = false;
        return;
      }
    },
    async deleteNoProduct(id) {
      const ok = await this.confirmDelete(
        'Tem a certeza que deseja eliminar este produto da lista de não produtos?'
      );
      if (!ok) return;
      this.loading.noProducts = true;
      try {
        await axios.delete(
          `${this.$store.state.apiUrl}feed/noProducts/${this.feed.name}/${this.feed.country}`,
          { data: { id_prod: id } }
        );
        await this.fetchNoProducts();
        this.loading.noProducts = false;
      } catch (error) {
        console.error('Error deleting no product:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao eliminar produto não incluído',
          text: 'Por favor, tente novamente mais tarde.',
        });
        this.loading.noProducts = false;
        return;
      }
    },
    async fetchManualPrices() {
      try {
        const { data } = await axios.get(
          `${this.$store.state.apiUrl}feed/manualPrices/${this.feed.name}/${this.feed.country}`
        );

        this.manualPrices = data.data.manual_prices.map((item) => ({
          ...item,
          end_date: item.end_date ? item.end_date.slice(0, 10) : null,
        }));
      } catch (error) {
        console.error('Error fetching manual prices:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao carregar preços manuais',
          text: 'Por favor, tente novamente mais tarde.',
        });
      }
    },
    async addManualPrice() {
      const ok = await this.confirmAdd(
        'Tem a certeza que deseja adicionar este preço manual?'
      );
      if (!ok) return;
      this.loading.manualPrices = true;
      try {
        await axios.post(
          `${this.$store.state.apiUrl}feed/manualPrices/${this.feed.name}/${this.feed.country}`,
          {
            sku: this.newManualPrice.sku,
            price: this.newManualPrice.price,
            end_date: this.newManualPrice.end_date || null,
          }
        );
        this.newManualPrice = { sku: '', price: null, end_date: null };
        await this.fetchManualPrices();
      } catch (error) {
        console.error('Error adding manual price:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao adicionar preço manual',
          text: 'Por favor, verifique o SKU e tente novamente.',
        });
      }
      this.loading.manualPrices = false;
    },
    async updateManualPrice(prod) {
      const ok = await this.confirmUpdate(
        'Tem a certeza que deseja atualizar este preço manual?'
      );
      if (!ok) return;
      this.loading.manualPrices = true;
      try {
        await axios.patch(
          `${this.$store.state.apiUrl}feed/manualPrices/${this.feed.name}/${this.feed.country}`,
          {
            id_prod: prod.id_prod,
            price: prod.manual_price,
            end_date: prod.end_date || null,
          }
        );
        this.$swal.fire({
          toast: true,
          icon: 'success',
          title: 'Preço manual atualizado',
          timer: 2000,
          position: 'top-end',
        });
      } catch (error) {
        console.error('Error updating manual price:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao atualizar preço manual',
          text: 'Por favor, tente novamente mais tarde.',
        });
      }
      this.loading.manualPrices = false;
    },
    async deleteManualPrice(id) {
      const ok = await this.confirmDelete(
        'Tem a certeza que deseja eliminar este preço manual?'
      );
      if (!ok) return;
      this.loading.manualPrices = true;
      try {
        await axios.delete(
          `${this.$store.state.apiUrl}feed/manualPrices/${this.feed.name}/${this.feed.country}`,
          { data: { id_prod: id } }
        );
        await this.fetchManualPrices();
      } catch (error) {
        console.error('Error deleting manual price:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao eliminar preço manual',
          text: 'Por favor, tente novamente mais tarde.',
        });
      }
      this.loading.manualPrices = false;
    },
  },
  created() {
    this.fetchFeeds().then(() => this.loadAll());
  },
};
</script>

<style scoped>
.feed-config-page {
  font-family: 'Poppins', sans-serif;
  position: relative;
}
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  z-index: 10;
}
.section-loader {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.6);
  z-index: 5;
}
.accordion-item {
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 10px;
  overflow: hidden;
}
.accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: #f7f7f7;
  cursor: pointer;
}
.accordion-body {
  padding: 10px;
  background: white;
}
.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s ease;
}
.accordion-enter,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
  padding: 0;
}
.card-body.py-3 {
  padding-top: 1rem;
  padding-bottom: 1rem;
}
</style>
